// src/App.js
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "./App.css";
import { QRCodeSVG } from "qrcode.react";
import { ethers } from "ethers";
import Swal from "sweetalert2";
import confetti from "canvas-confetti";

const USDT_CONTRACT = "0x55d398326f99059fF775485246999027B3197955";
const MORALIS_API_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6IjBjOGI5Zjg2LTkwNDUtNDNiYi04MmZhLTVhYTUzZWY1MzBkMSIsIm9yZ0lkIjoiNDg0MzE2IiwidXNlcklkIjoiNDk4MjczIiwidHlwZUlkIjoiNjg3NmQyMmUtMjBlNi00YzIzLWE5YmQtZGQ5MWVmZGQxY2ExIiwidHlwZSI6IlBST0pFQ1QiLCJpYXQiOjE3NjQ3NDA1NTEsImV4cCI6NDkyMDUwMDU1MX0.Y8tH-TtlKBtDTf6xgAnAytitaRXEEjgQAsqf8YpX7os";

function App() {
  const [depositData, setDepositData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [txHash, setTxHash] = useState("");
  const [timeLeft, setTimeLeft] = useState(10 * 60);
  const [txHistory, setTxHistory] = useState([]);
  const [trackingStatus, setTrackingStatus] = useState("Starting...");
  const [timeOut, setTimeOut] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  const provider = new ethers.JsonRpcProvider("https://bsc-dataseed.binance.org");

  // Fetch Deposit Data
  const fetchDeposit = useCallback(async (authToken, isFirstLoad = false) => {
    const formData = new FormData();
    formData.append("token", authToken);

    try {
      const res = await axios.post("https://api.zipwallet.net/api/deposit-update", formData);

      const data = res.data;

      setTimeOut(data.data.expire_timestamp);

      setDepositData(data);

      if (isFirstLoad) {
        setIsLoading(false);
      }

      console.log(data.data);

      if (data.response_code === "200") {
        setPaymentStatus("confirmed");

        // Trigger confetti animation
        const duration = 3 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        const interval = setInterval(() => {
          const timeLeft = animationEnd - Date.now();
          if (timeLeft <= 0) return clearInterval(interval);

          const particleCount = 50 * (timeLeft / duration);
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
          });
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
          });
        }, 250);

        // Store backUrl for guaranteed redirect
        const backUrl = data.data.backUrl;

        // Ensure redirect happens after 3 seconds in ANY condition
        const redirectTimer = setTimeout(() => {
          if (backUrl) {
            window.location.href = backUrl;
          }
        }, 3000);

        // Show SweetAlert with success message
        Swal.fire({
          title: 'Payment Success!',
          text: 'Your transaction has been confirmed successfully',
          icon: 'success',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
          allowOutsideClick: false,
          willClose: () => {
            // Ensure redirect happens even if SweetAlert closes early
            if (backUrl) {
              window.location.href = backUrl;
            }
          }
        }).catch(() => {
          // If SweetAlert fails, the setTimeout will still handle redirect
          console.log('SweetAlert error, but redirect timer is active');
        });

        return true; // Signal to stop polling
      }
      return false;


    } catch (err) {
      console.error(err);
      setTrackingStatus("API Error");
      setIsLoading(false);
    }
  }, []);

  // On Load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get("authToken");

    if (!authToken) {
      alert("Invalid link!");
      return;
    }

    if (Notification.permission === "default") {
      Notification.requestPermission();
    }

    fetchDeposit(authToken, true);

    // Poll every 5 seconds
    const pollInterval = setInterval(async () => {
      const shouldStop = await fetchDeposit(authToken, false);
      if (shouldStop) {
        clearInterval(pollInterval);
      }
    }, 5000);

    return () => {
      if (window.unwatchPending) window.unwatchPending();
      clearInterval(pollInterval);
    };
  }, [fetchDeposit]);

  // Timer
  useEffect(() => {
    if (!timeOut || paymentStatus === "confirmed") return;

    const t = setInterval(() => {
      const currentTimestamp = Math.floor(Date.now() / 1000);
      const remaining = timeOut - currentTimestamp;
      setTimeLeft(remaining > 0 ? remaining : 0);
    }, 1000);

    return () => clearInterval(t);
  }, [timeOut, paymentStatus]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };


  const copyAddress = () => {
    navigator.clipboard.writeText(depositData.data.to_address || "");
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };


  // ------------------------------ UI -------------------------------------
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center min-vh-100"
        style={{ background: "linear-gradient(102.05deg, #3e28a1 40.6%, #4b33b5 91.37%)" }}
      >
        <div className="text-center text-white">
          <div className="spinner-border" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4 className="mt-3">Loading Payment Details...</h4>
        </div>
      </div>
    );
  }

  return (
    <div
      className="d-flex justify-content-center align-items-center min-vh-100 p-3"
      style={{ background: "linear-gradient(102.05deg, #3e28a1 40.6%, #4b33b5 91.37%)" }}
    >
      <div className="border rounded w-100 shadow-lg" style={{ maxWidth: 850, backgroundColor: "white" }}>
        <div className="p-3 p-md-4">

          {/* Header */}
          <div className="mb-4">
            <div className="card" style={{ backgroundColor: "#4219fa", border: "none" }}>
              <div className="card-body d-flex align-items-center justify-content-center justify-content-md-start p-3 text-white rounded">
                <img
                  src="https://zipwallet.net/img/Zip-Wallet-2.png"
                  alt="Logo"
                  style={{ width: 50, height: 50, borderRadius: "50%", marginRight: 15 }}
                />
                <h2 className="m-0">ZIP Wallet</h2>
              </div>
            </div>
          </div>

          <div className="row align-items-center">
            {/* QR Code Section */}
            <div className="col-12 col-md-5 text-center mb-4 mb-md-0">
              {paymentStatus === "pending" ? (
                <div className="p-3 bg-light rounded d-inline-block">
                  <QRCodeSVG
                    value={depositData.data.to_address || ""}
                    size={200}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="H"
                  />
                </div>
              ) : (
                <div className="text-success fs-1 fw-bold">
                  <div style={{ fontSize: "4rem" }}>✓</div>
                  Payment Confirmed!
                </div>
              )}
            </div>

            {/* Payment Details Section */}
            <div className="col-12 col-md-7">
              <h5 className="mb-3 text-center text-md-start text-dark">Pay With QR Code OR Wallet Address</h5>

              <div className="mb-3 text-center text-md-start">
                <span className="badge bg-success fs-6">{trackingStatus}</span>
              </div>

              <div className="mb-2 text-dark text-center text-md-start">
                <p className="mb-2">
                  Deposit Asset: <strong className="text-primary">{depositData.data.deposit_asset}</strong>
                </p>
                <p className="mb-3">
                  Network: <strong className="text-primary">{depositData.data.network_type}</strong>
                </p>
              </div>

              {/* Wallet Address */}
              <div className="mb-3">
                <div className="p-2 bg-light rounded text-center text-md-start">
                  <small className="text-dark" style={{ wordBreak: "break-all", fontSize: "0.85rem" }}>
                    {depositData.data.to_address || "Loading..."}
                  </small>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-2 flex-wrap justify-content-center justify-content-md-start mb-3">
                <button
                  onClick={copyAddress}
                  className={`btn ${isCopied ? 'btn-success' : 'btn-primary'} d-flex align-items-center gap-2`}
                  style={{ minWidth: "140px" }}
                >
                  {isCopied ? (
                    <>
                      <span>✓</span>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <span>📋</span>
                      <span>Copy Address</span>
                    </>
                  )}
                </button>

                {txHash && (
                  <a
                    href={`https://bscscan.com/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-success"
                  >
                    View Transaction
                  </a>
                )}
              </div>

              {txHistory.length > 0 && (
                <div className="mt-4">
                  <h6 className="text-dark text-center text-md-start">Recent Deposits</h6>
                  {txHistory.slice(0, 3).map((t, i) => (
                    <div key={i} className="border-bottom py-2 small">
                      <div className="d-flex justify-content-between">
                        <span className="text-dark">{t.value.toFixed(4)} {t.asset}</span>
                        <a href={`https://bscscan.com/tx/${t.txHash}`} target="_blank" rel="noreferrer">View</a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Amount and Timer Section */}
          <div className="col-12 text-center mt-4 pt-3 border-top">
            <h4 className="text-dark mb-3">
              Amount:{" "}
              <span className="text-primary fw-bold">
                {depositData.data.deposit_amount} {depositData.data.deposit_asset}
              </span>
            </h4>

            <div className="mb-3">
              <span className="badge bg-danger fs-5 px-3 py-2">
                Expires in: {formatTime(timeLeft)}
              </span>
            </div>

            {paymentStatus === "confirmed" ? (
              <div className="alert alert-success fs-5 fw-bold">
                Payment Received Successfully!
              </div>
            ) : (
              <div className="text-warning fw-bold">
                {depositData.message || "Waiting for payment..."}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default App;
